import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { initializeCancelBookingModalAtom } from "../atoms/actions/initializeCancelBookingModalAtom";
import { PatronCancelBooking } from "../types";
import { useInitializeCancelBookingModal_Booking$key } from "./__generated__/useInitializeCancelBookingModal_Booking.graphql";

type Props = {
  bookingRef: useInitializeCancelBookingModal_Booking$key;
};

export const useInitializeCancelBookingModal = ({ bookingRef }: Props) => {
  const booking = useFragment(
    useInitializeCancelBookingModalFragmentString,
    bookingRef
  );
  const initializeCancelBookingModal = useSetAtom(
    initializeCancelBookingModalAtom
  );
  const ownerClientId = booking.ownerClient?.id ?? "";

  const isDurationBasedFacility =
    booking.startSlot?.agenda?.facility?.type !== "GOLF_COURSE";

  const isLessonBooking = booking.__typename === "LessonBooking";

  const startSlotId = booking.startSlot?.id;

  const mappedBooking: PatronCancelBooking.Booking = useMemo(() => {
    return {
      id: booking.id,
      durationMins: booking.durationMins ?? null,
      startSlotId: startSlotId ?? null,
      type: isLessonBooking
        ? "Lesson"
        : isDurationBasedFacility
        ? "Duration"
        : "Golf",
      rounds:
        booking.rounds?.edges?.flatMap((edge, index) => {
          const round = edge?.node;
          if (!round) return [];

          const player: PatronCancelBooking.RoundPlayer = round.player
            ? {
                id: round.player.id,
                clientType:
                  round.__typename === "MemberRound"
                    ? "member"
                    : round.__typename === "GuestRound"
                    ? "guest"
                    : "member",
                firstName: round.player.firstName ?? "",
                lastName: round.player.lastName ?? "",
                profileImagePublicId:
                  round.player.profileImage?.publicId ?? null,
              }
            : {
                id: null,
                clientType:
                  round.__typename === "MemberRound"
                    ? "memberHold"
                    : "unknownGuest",
                firstName: null,
                lastName: null,
                profileImagePublicId: null,
              };

          let editingRound: PatronCancelBooking.Round;

          if (isDurationBasedFacility) {
            const durationRound: PatronCancelBooking.DurationRound = {
              localId: round.id,
              serverId: round.id,
              index,
              player,

              isOwner: !!(
                round.player?.id && round.player?.id === ownerClientId
              ),
              isNonPlayingHost: round.nonPlayingHost ?? false,
              type: "Duration",
            };
            editingRound = durationRound;
          } else {
            const golfRound: PatronCancelBooking.GolfRound = {
              localId: round.id,
              serverId: round.id,
              index,
              player,
              isOwner: !!(
                round.player?.id && round.player?.id === ownerClientId
              ),
              isNonPlayingHost: round.nonPlayingHost ?? false,
              type: "Golf",
              courseLayout: round.courseLayout?.id
                ? {
                    id: round.courseLayout.id,
                  }
                : null,
              transportationType: round.transportationType
                ? {
                    id: round.transportationType.id,
                  }
                : null,
            };
            editingRound = golfRound;
          }

          return editingRound ? [editingRound] : [];
        }) ?? [],
    };
  }, [
    booking.id,
    booking.durationMins,
    booking.rounds?.edges,
    startSlotId,
    isLessonBooking,
    isDurationBasedFacility,
    ownerClientId,
  ]);

  useEffect(() => {
    if (!startSlotId) return;
    initializeCancelBookingModal({
      booking: mappedBooking,
    });
  }, [mappedBooking, initializeCancelBookingModal, ownerClientId, startSlotId]);
};
const useInitializeCancelBookingModalFragmentString = graphql`
  fragment useInitializeCancelBookingModal_Booking on Booking {
    id
    __typename
    ... on StandardBooking {
      durationMins
    }
    ownerClient {
      ... on Member {
        id
      }
    }
    startSlot {
      id
      agenda {
        facility {
          type
        }
      }
    }
    rounds {
      edges {
        node {
          id
          __typename
          transportationType {
            id
          }
          courseLayout {
            id
          }
          player {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          ... on MemberRound {
            nonPlayingHost
          }
        }
      }
    }
  }
`;
